// Background video for container blocks: muted loop that plays only while on screen.
// Reduced-motion visitors never get playback; the background image stays visible instead.
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class SBgVideo extends HTMLElement {
  #observer;
  #ac;
  #inView = false;

  connectedCallback() {
    this.video = this.querySelector('[data-bg-video]');
    if (!this.video) return;
    // Safari only autoplays when muted is set as both the property and the attribute.
    this.video.muted = true;
    this.video.defaultMuted = true;
    this.video.setAttribute('muted', '');
    this.video.playsInline = true;
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    if (reduceMotion.matches) {
      this.video.removeAttribute('autoplay');
      this.video.pause();
    }

    const markPlaying = () => {
      if (!this.video.paused && this.video.currentTime > 0) this.video.setAttribute('data-playing', '');
    };
    // Phones can start the video before this script loads, so the "playing" event is missed.
    // Also reveal the video once frames are actually advancing, and check its current state now.
    this.video.addEventListener('playing', () => this.video.setAttribute('data-playing', ''), { signal });
    this.video.addEventListener('timeupdate', markPlaying, { signal });
    markPlaying();

    const tryPlay = () => {
      if (this.#inView && !reduceMotion.matches && this.video.paused) this.video.play().catch(() => {});
    };
    // Safari can reject the first play() before data is ready; retry once it can play.
    this.video.addEventListener('canplay', tryPlay, { signal });
    this.video.addEventListener('loadeddata', tryPlay, { signal });
    // Some phones (e.g. iPhone Low Power Mode) block autoplay until the visitor touches the page.
    for (const type of ['touchstart', 'pointerdown', 'scroll']) {
      document.addEventListener(type, tryPlay, { signal, passive: true });
    }
    reduceMotion.addEventListener('change', () => {
      if (reduceMotion.matches) this.#stop();
    }, { signal });

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        this.#inView = entry.isIntersecting;
        if (entry.isIntersecting && !reduceMotion.matches) {
          if (this.video.readyState === 0 && this.video.networkState !== this.video.NETWORK_LOADING) this.video.load();
          this.video.play().catch(() => {});
        } else if (!this.video.paused) {
          this.video.pause();
        }
      }
    }, { threshold: 0.15 });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#ac?.abort();
  }

  #stop() {
    this.video.pause();
    this.video.removeAttribute('data-playing');
  }
}

if (!customElements.get('s-bg-video')) customElements.define('s-bg-video', SBgVideo);
